<script lang="ts">
    import { TRANSITION_DELAY } from '$lib/global/constants';
    import { PREVIEW_MODE } from '$lib/global/constants';

    import { afterNavigate, beforeNavigate } from '$app/navigation';
    import { page } from '$app/stores';
    import AffiliateBadge from '$lib/components/AffiliateBadge.svelte';
    import CookieOverlay from '$lib/components/ConsentOverlay/ConsentOverlay.svelte';
    import CustomHeader from '$lib/components/CustomHeader/CustomHeader.svelte';
    import Footer from '$lib/components/Footer/Footer.svelte';
    import Header from '$lib/components/Header.svelte';
    import ProgressBar from '$lib/components/Progressbar.svelte';
    import '../app.css';
    import { initConsentGate } from '$lib/global/consent/consentGate';
    import { resolveBrandFontWeightVar } from '$lib/global/helper/brand-font-weight';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import {
        createTrackingService,
        getTrackingService,
    } from '$lib/global/tracking/TrackingServiceInstance';
    import { getCookiePreferences, getFontName, getHeadingFontName } from '$lib/helper';
    import BrowserStorage from '$lib/Stores/BrowserStorage.svelte';
    import { embedOptions, quizMetrics, media, isFirstPageLoad } from '$lib/Stores/stores';
    import { createGoToUrlWithPreservedQueries } from '$lib/utils/createGoToUrlWithPreservedQueries';
    import { createPageGuard } from '$lib/utils/createPageGuard';
    import { handleViewportHeight } from '$lib/utils/handleViewportHeight';
    import { initEmbedHeightObserver, reportHeight } from '$lib/utils/reportEmbedHeight';

    import { init } from 'emoji-mart';
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { ModalContainer } from 'svelte-promise-modals';

    import type { LayoutData, Media } from '$lib/types/LayoutData';
    import type { QuizMetric } from '$lib/types/QuizMetrics';
    import type { Theme, ThemeV2 } from '$lib/types/Theme';

    import 'svelte-promise-modals/style.css';

    // from +layout.js
    export let data: LayoutData;

    // CAP-4147: must run before createTrackingService — the tracking bootstrap asks the
    // gate whether it may touch cookies/localStorage or load Segment's analytics.js.
    // embedOptions is populated by +layout.ts load() before this component initializes.
    // getCookiePreferences never throws and always returns a plain object, so a corrupted
    // stored decision cannot take down layout initialization.
    initConsentGate({
        bannerLayout: data.cookieOverlay?.layout ?? 'none',
        bannerEnabled: get(embedOptions).cookieBanner,
        isPreview: get(embedOptions).preview,
        storedPreferences: getCookiePreferences(data.campaignId, data.campaignVersionId),
    });

    createTrackingService(
        {
            campaignId: data.campaignId,
            companyId: data.companyId,
            workspaceId: data.workspaceId,
            campaignVersionId: data.campaignVersionId,
            trackingVersion: data.analytics?.trackingVersion,
            preventTracking: data.analytics?.preventSegment ?? false,
            analyticsLink: data.analytics?.analyticsLink ?? '',
        },
        data.pages,
        $page.data.fields,
    );

    let mounted = false;

    const fontName = getFontName(data.theme as Theme);
    const headingFontName = getHeadingFontName(data.theme as ThemeV2);

    handleViewportHeight();

    // The first client-side navigation means we're past the initial cold load, so
    // entrance animations can play again from here on. `beforeNavigate` fires before
    // the next page's blocks are constructed (unlike `afterNavigate`), avoiding an
    // off-by-one where the first in-funnel page wouldn't animate. It is not called on
    // the initial load, so the cold load keeps `isFirstPageLoad === true`.
    beforeNavigate(({ type }) => {
        if (type !== 'leave') {
            isFirstPageLoad.set(false);
        }
    });

    const routeGuard = createPageGuard();
    afterNavigate(() => {
        if (routeGuard($page.params.slug)) {
            return;
        }

        const resolved = getTrackingService().resolveDisplayData($page.data);
        getTrackingService().handleRouteChange($page.params.slug, {
            pageSlug: resolved.pageSlug,
            pageId: resolved.pageId,
            pageName: resolved.pageName,
            tag: resolved.tag,
            isHomePage: resolved.isHomePage,
            isLastPage: resolved.isLastPage,
        });

        if (PREVIEW_MODE) {
            const slug = $page.params.slug ?? 'index';
            window.dispatchEvent(new CustomEvent('funnel:navigate', { detail: { slug } }));
        }

        // Report embed height after navigation to ensure the parent iframe
        // receives the updated (potentially smaller) content height
        if ($embedOptions.autoHeight) {
            requestAnimationFrame(() => {
                reportHeight();
            });
        }
    });

    const loadQuizMetrics = async () => {
        try {
            const response = await fetch(data.quizDataUrl);
            const metrics = (await response.json()) as QuizMetric[];

            if (metrics) {
                quizMetrics.set(metrics);
            }
        } catch (err) {
            console.warn('Error fetching Quiz metrics', {
                url: data.quizDataUrl,
                error: err,
            });
        }
    };

    const initMediaStore = () => {
        // Create object with mediaId: mediaData pairs
        const mediaMap = data.media.reduce((acc: Record<string, Media>, mediaItem) => {
            acc[mediaItem._id] = mediaItem;
            return acc;
        }, {});

        media.set(mediaMap);
    };

    onMount(() => {
        if (PREVIEW_MODE) {
            const nextPageNavigator = new NextPageNavigator(
                $page.data.campaignId,
                $page.data.firstResultPage,
                $page.data.pageIdSlugMap,
                $page.data.trackingProperties,
            );

            window.perspective = {
                ...window.perspective,
                navigateToPageBySlug: (pageSlug: string) => {
                    nextPageNavigator.go(pageSlug);
                },
                theme: data.theme,
            };

            window.dispatchEvent(new CustomEvent('funnel:ready'));
        }

        // Init Emojimart
        init({ data: data.emojiData });

        if ($embedOptions.autoHeight) {
            initEmbedHeightObserver();
        }

        // Fetch Quiz reveal metrics
        if (data.hasQuizReveal) {
            void loadQuizMetrics();
        }

        // Init media store
        if (data.media) {
            initMediaStore();
        }

        mounted = true;
    });

    const handleLogoClick = () => {
        if (PREVIEW_MODE) {
            const gotoUrl = new URL(data.homeLink, window.location.origin);
            const { url } = createGoToUrlWithPreservedQueries(gotoUrl.toString());
            window.location.replace(url);
            return;
        }
        window.location.replace(data.homeLink);
    };
</script>

<svelte:head>
    <meta name="theme-color" content={data.theme.backgroundColor} />
    <!-- Extends styles to apply to <div id='#perspective-frame-content'/> part of Preview feature -->
    {@html `<style>
        body, html, #perspective-frame-content {
            background-color: ${data.theme.backgroundColor};
            ${$embedOptions.autoHeight ? 'overflow: hidden;' : ''}
        }
    </style>`}
</svelte:head>

<!-- Don't add any parent to this element because the preview app styles are limited to #funnel-app-main selector -->
<main
    id="funnel-app-main"
    style="--font-family: 'Twemoji Country Flags', '{fontName}'; --heading-font-family: 'Twemoji Country Flags', '{headingFontName ??
        fontName}'; --brand-body-font-weight: {resolveBrandFontWeightVar(
        data.theme.bodyWeight,
    )}; --brand-heading-font-weight: {resolveBrandFontWeightVar(data.theme.headingWeight)}"
    class="fun-flex fun-flex-col"
    class:fun-min-h-screen={!$embedOptions.autoHeight}
>
    {#if $embedOptions.header}
        {#if data.customHeader && data.customHeader.length > 0}
            <CustomHeader
                bgColor={data.header.bgColor}
                fixed={data.header.fixed}
                translucent={data.header.translucent}
                components={data.customHeader}
                align={data.header.align}
                onLogoClick={handleLogoClick}
                hidden={data.header.hidden}
            />
        {:else}
            <Header
                src={data.header.logo}
                align={data.header.align}
                size={data.header.size}
                onClick={handleLogoClick}
                bgColor={data.header.bgColor}
                showIcon={data.header.showIcon}
            />
        {/if}
    {/if}

    {#if mounted}
        <div class="fun-flex-1">
            <slot />
        </div>
    {/if}

    {#if $embedOptions.footer}
        <Footer
            campaignId={data.campaignId}
            campaignVersionId={data.campaignVersionId}
            cookieOverlay={data.cookieOverlay}
            integrations={data.integrations}
            legalLink={data.footer.legalLink}
            legalText={data.footer.legalText}
            termsLink={data.footer.termsLink}
            termsText={data.footer.termsText}
            language={data.cookieOverlay.language}
            bgColorReference={!data?.footer?.box?.backgroundColor ||
            data?.footer?.box?.backgroundColor === 'transparent'
                ? data.theme.backgroundColor
                : data?.footer?.box?.backgroundColor}
            branding={data.footer.branding}
            transitionDelay={$page?.data?.components?.length * TRANSITION_DELAY}
            transitionEffect={data.theme.transition}
            box={data.footer.box}
            theme={data.theme}
        />
    {/if}

    {#if data.showProgressBar}
        <ProgressBar
            sortedPages={data.sortedPages}
            pageId={$page.data.pageId}
            color={data.theme.buttonBackgroundColor}
            hasResultPages={data.hasResultPages}
        />
    {/if}

    {#if data.showAffiliateBadge}
        <AffiliateBadge
            link={$page.data.affiliateLink}
            campaignId={$page.data.campaignId}
            companyId={$page.data.companyId}
        />
    {/if}

    <div id="ps-cookie-banner-wrapper" class="fun-overflow-hidden">
        {#if $embedOptions.cookieBanner}
            <CookieOverlay
                layout={data.cookieOverlay.layout}
                language={data.cookieOverlay.language}
                integrations={data.integrations}
                legalLink={data.footer.legalLink}
                termsLink={data.footer.termsLink}
                campaignId={data.campaignId}
                campaignVersionId={data.campaignVersionId}
            />
        {/if}
    </div>

    <ModalContainer />
</main>

<BrowserStorage campaignId={data.campaignId} />

<style>
    main {
        font-family:
            var(--font-family),
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            Roboto,
            'Helvetica Neue',
            Arial,
            'Noto Sans',
            sans-serif,
            'Apple Color Emoji',
            'Segoe UI Emoji',
            'Segoe UI Symbol',
            'Noto Color Emoji';
    }
</style>
